<template>
    <div id="registration">
        <div class="left-col">
            <label for="name" class="label-default">Imię <span class="required-star">*</span>
                <span class="errors">{{ errors.name }}</span>
            </label>
            <input id="name" class="input-default" :class="{'input-error': errors.name}" type="text"
                   @keyup="debounce(validateRequiredField, 'name')" @blur="validateRequiredField('name')"
                   v-model="patient.name" autocomplete="off">

            <label for="surname" class="label-default">Nazwisko <span class="required-star">*</span>
                <span class="errors">{{ errors.surname }}</span>
            </label>
            <input id="surname" class="input-default" :class="{'input-error': errors.surname}" type="text"
                   @keyup="debounce(validateRequiredField, 'surname')" @blur="validateRequiredField('surname')"
                   v-model="patient.surname" autocomplete="off">

            <label for="telephone" class="label-default">Telefon <span class="required-star">*</span>
                <span class="errors">{{ errors.telephone }}</span>
            </label>
            <input id="telephone" class="input-default" :class="{'input-error': errors.telephone}" type="text"
                   @keyup="debounce(validateTelephoneField)" @blur="validateTelephoneField"
                   v-model="patient.telephone" autocomplete="off">

            <label for="email" class="label-default">E-mail <span class="required-star">*</span>
                <span class="errors">{{ errors.email }}</span>
            </label>
            <input id="email" class="input-default" :class="{'input-error': errors.email}" type="email"
                   @keyup="debounce(validateEmailField)" @blur="validateEmailField" v-model="patient.email">
            <div class="duplicated-email-info" v-if="emailWarning.show">
                <span>{{ emailWarning.shortInfo }}</span>
                <span @click="handleEmailWarningAction"><b><u>Kliknij tutaj,</u></b></span>
              <span> {{ emailWarning.detailInfo }}</span>
            </div>
        </div>
        <div class="right-col">
            <!-- Hard spaces added for proper alignment-->

            <label class="label-default">&nbsp;<span class="errors">{{ errors.password }}</span></label>
            <input placeholder="Wpisz hasło..." class="input-default input-password" type="password"
                   :class="{'input-error': errors.password}" v-model="patient.password"
                   @keyup="debounce(validatePasswordField)" @blur="validatePasswordField"
                   autocomplete="off">

            <label class="label-default">&nbsp;<span class="errors">{{ errors.passwordConfirm }}</span></label>
            <input placeholder="Potwierdź hasło..." class="input-default input-password" type="password"
                   :class="{'input-error': errors.passwordConfirm}" v-model="patient.passwordConfirm"
                   @keyup="debounce(validatePasswordConfirmField)" @blur="validatePasswordConfirmField"
                   autocomplete="off">

            <div class="rules-container">
                <input id="rules-agreement" class="checkbox-blue" type="checkbox" v-model="agreement">
                <label for="rules-agreement">
                    Akceptuję <a :href="RULES_PDF_URL" class="rules-download" download>regulamin</a>
                    <span class="required-star">*</span>
                </label>
            </div>
            <button class="btn-solid-orange btn-register" @click="register">ZAREJESTRUJ SIĘ</button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../css/colors.scss";
@import "../../css/constants.scss";

$text-color: #4fb696;

#registration {
    color: $text-color;
    display: flex;
    min-height: $bg-min-height;

    .left-col,
    .right-col {
        display: flex;
        flex-flow: column;
        width: 100%;
        min-width: 250px;
        max-width: 400px;
    }
    .left-col {
        margin-right: 2em;
        margin-left: auto;
    }
    .right-col {
        margin-right: auto;
        .input-password {
            padding-left: 45px;
            background: white url('../../images/password-icon.png') no-repeat 10px 50%;

            &.input-error {
                background: $error-bg-color url('../../images/password-icon.png') no-repeat 10px 50%;
            }
        }
        .rules-container {
            font-size: 15px;
            font-weight: 500;
            margin-top: 0.2em;

            .rules-download {
                color: $text-color;
                text-decoration: underline;
            }
        }
        .btn-register {
            font-weight: 500;
            max-width: 160px;
            margin-left: 22px;
            margin-top: 1em;
        }
    }
    .input-default {
        min-width: initial;
        flex-grow: 1;
        max-height: 17px;
    }
    @media all and (max-width: 570px) {
        flex-flow: column;

        .left-col,
        .right-col {
            margin: auto;
        }
    }

    .duplicated-email-info {
        color: red;

        & u {
            color: red;
            cursor: pointer;
        }
    }
}
</style>

<script>
import FreeTermConfirmation from './FreeTermConfirmation.vue'
import ApiMixin from '../mixins/ApiMixin.js'

import {
    FIELD_ERRORS,
    RULES_PDF_URL,
    PASSWORD_MIN_LENGTH,
    API_PASSWORD_RESET_URL,
    API_RESEND_ACTIVATION_LINK
} from '../constants.js'
import {
    isValidPhoneNumber,
    isValidEmail,
} from '../utils.js'

const PATIENT_STUB = {
    name: '',
    surname: '',
    telephone: '',
    email: '',

    password: '',
    passwordConfirm: '',
};

const EMAIL_WARNING_STUB = {
    show: false,
    shortInfo: '',
    detailInfo: '',
    actionUrl: '',
};

export default {
    name: 'Registration',
    mixins: [
        ApiMixin,
    ],
    data() {
        return {
            patient: {...PATIENT_STUB},
            errors: {...PATIENT_STUB},
            agreement: false,
            emailWarning: {...EMAIL_WARNING_STUB},

            RULES_PDF_URL,
        }
    },
    computed: {
        freeTerm() {
            return this.$store['FreeTerm-chosenFreeTerm'];
        },
        facilityName() {
            return this.$store.facility.name;
        },
        facilityAddress() {
            const fac = this.$store.facility;
            return `${fac.city} ul. ${fac.street} ${fac.house_number}${fac.flat_number ? '/' + fac.flat_number : ''}`;
        },
    },
    methods: {
        handleEmailWarningAction() {
            const headers = {'Content-Type': 'application/x-www-form-urlencoded'};
            const successHandler = () => {
                this.$app.showSuccessAlert('Na podany adres e-mail wysłane zostały dalsze instrukcje.');
                this.$router.push({name: 'index'});
            }
            const failureHandler = () => {
                this.$app.showFailureAlert('Podczas wysyłania wiadomości e-mail wystąpił błąd, spróbuj ponownie.')
            }
            let url = '';
            if (this.emailWarning.actionUrl === 'password-recovery') {
                url = API_PASSWORD_RESET_URL;
            } else {
                url = `${API_RESEND_ACTIVATION_LINK}${this.$app.$facility}/`;
            }
            this.$http.post(url, `email=${this.patient.email}`, {headers}).then(
                successHandler, failureHandler
            );
        },
        isEmailUnique() {
            const checkEmailUrl = this.$api.facility.detailCheckEmailUniqueness(this.$app.$facility);
            this.$http.get(`${checkEmailUrl}?email=${this.patient.email}`).then((response) => {
                if (response.status === 200) {
                    this.emailWarning.shortInfo = response.body.short_info;
                    this.emailWarning.detailInfo = response.body.detail_info;
                    this.emailWarning.actionUrl = response.body.action_url;
                    this.emailWarning.show = true;
                } else if (response.status === 204) {
                  this.emailWarning.show = false;
                  this.emailWarning = {...EMAIL_WARNING_STUB};
                }
            })
            return '';
        },
        validateRequiredField(field) {
            this.errors[field] = this.patient[field] ? '' : FIELD_ERRORS.REQUIRED;
        },
        validateTelephoneField() {
            this.errors.telephone = !this.patient.telephone
                ? FIELD_ERRORS.REQUIRED
                : !isValidPhoneNumber(this.patient.telephone)
                    ? FIELD_ERRORS.TELEPHONE
                    : '';
        },
        validateEmailField() {
            this.errors.email = !this.patient.email
                ? FIELD_ERRORS.REQUIRED
                : !isValidEmail(this.patient.email)
                    ? FIELD_ERRORS.EMAIL
                    : this.isEmailUnique();
        },
        validatePasswordField() {
            this.errors.password = !this.patient.password
                ? FIELD_ERRORS.REQUIRED
                : this.patient.password.length < PASSWORD_MIN_LENGTH
                    ? FIELD_ERRORS.PASSWORD_TOO_SHORT
                    : '';
        },
        validatePasswordConfirmField() {
            this.errors.passwordConfirm = !this.patient.passwordConfirm
                ? FIELD_ERRORS.REQUIRED
                : this.patient.password !== this.patient.passwordConfirm
                    ? FIELD_ERRORS.PASSWORDS_DIFFER
                    : '';
        },
        register() {
            if (!this.freeTerm) {
                this.$app.showFailureAlert('Przed rejestracją należy wybrać termin do rezerwacji.');
                return;
            }
            this.validateRequiredField('name');
            this.validateRequiredField('surname');
            this.validateTelephoneField();
            this.validateEmailField();
            this.validatePasswordField();
            this.validatePasswordConfirmField();

            // All fields ale required.
            if (Object.values(this.errors).some(field => field === FIELD_ERRORS.REQUIRED)) return;

            // Show alert if checkboxes aren't ticked.
            if (!this.agreement) {
                this.$app.showFailureAlert('Wszystkie pola są wymagane');
                return;
            }

            const url = this.$api.facility.detailRegisterPatient(this.$app.$facility);
            // Convert data format for API.
            const data = {...this.patient, 'free_term': this.freeTerm,
                'password_confirm': this.patient.passwordConfirm};
            delete data.passwordConfirm;

            this.$app.showLoader();
            this.$http.post(url, data).then(response => {
                this.$bus.$emit('$store:setPatient', response.data)
                FreeTermConfirmation.methods.confirmFreeTerm(this.freeTerm, this, true);
            }, () => {
                this.$app.hideLoader();
                this.$app.showFailureAlert('Wystąpił błąd podczas rejestracji.');
                this.$router.push({name: 'index'});
            });
        },
    },
    mounted() {
        this.$bus.$emit('TopBar:setExtraHeading', 'REJESTRACJA');
    },
    beforeDestroy() {
        this.$bus.$emit('TopBar:setExtraHeading', '');
    },
}
</script>



// WEBPACK FOOTER //
// Registration.vue?80eca3ee